import React, { useEffect, useContext, useRef, useState, useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { AccessContext } from '../contexts/AccessProvider';
import useAssetAiChat from '../hooks/useAssetAiChat';
import useAssetMediaPublic from '../hooks/useAssetMediaPublic';
import ErrorView from '../components/errors/ErrorView';
import LoadingView from '../../shared/components/LoadingView';
import FormInput from '../../shared/components/FormInput';

const DEFAULT_STARTER_PROMPTS = [
  'What manuals are available?',
  'Summarize open tasks on this asset',
  'What maintenance was done recently?',
  'Which workflows can I start myself?',
  'What is the warranty status?',
];

function renderAssistantContent(text) {
  if (!text) return null;
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, idx) => {
    const lines = block.split('\n');
    const isList = lines.every((l) => /^\s*[-*•]\s+/.test(l) || l.trim() === '');
    if (isList && lines.some((l) => /^\s*[-*•]\s+/.test(l))) {
      return (
        <ul key={idx} className="mb-2 ps-3">
          {lines.filter((l) => l.trim()).map((line, i) => (
            <li key={i}>{line.replace(/^\s*[-*•]\s+/, '')}</li>
          ))}
        </ul>
      );
    }
    if (lines.length > 1) {
      return (
        <div key={idx} className="bg-light border rounded p-2 mb-2 small">
          {lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      );
    }
    return <p key={idx} className="mb-2">{block}</p>;
  });
}

function sourceLabel(source) {
  const section = source.heading_path?.length
    ? source.heading_path.join(' › ')
    : null;
  if (source.filename && section) return `${source.filename} — ${section}`;
  return source.filename || source.breadcrumb || 'Document';
}

function ChatSources({ sources, onOpen, openingId }) {
  const [expandedFile, setExpandedFile] = useState(null);
  if (!sources?.length) return null;

  const grouped = new Map();
  const uniqueKeys = new Set();

  sources.forEach((src) => {
    const section = src.heading_path?.length ? src.heading_path.join(' › ') : '';
    const dedupeKey = `${src.media_id}|${src.filename || ''}|${section}|${src.breadcrumb || ''}`;
    if (uniqueKeys.has(dedupeKey)) return;
    uniqueKeys.add(dedupeKey);

    const fileKey = `${src.media_id}|${src.filename || 'Document'}`;
    if (!grouped.has(fileKey)) {
      grouped.set(fileKey, { mediaId: src.media_id, filename: src.filename || 'Document', items: [] });
    }
    grouped.get(fileKey).items.push(src);
  });

  const groupedSources = Array.from(grouped.values());

  return (
    <div className="mt-2 pt-2 border-top">
      <div className="small text-muted mb-1">Sources</div>
      <div className="d-flex flex-column gap-2">
        {groupedSources.map((group) => {
          const isOpen = expandedFile === group.filename;
          return (
            <div key={`${group.mediaId}-${group.filename}`} className="border rounded p-2 bg-light-subtle">
              <button
                type="button"
                className="btn btn-sm w-100 d-flex align-items-center justify-content-between px-1 py-1 text-start border-0 bg-transparent"
                onClick={() => setExpandedFile(isOpen ? null : group.filename)}
                aria-expanded={isOpen}
              >
                <span className="d-inline-flex align-items-center small fw-semibold text-body">
                  <i className="bi bi-file-earmark-text me-2 text-secondary" />
                  {group.filename}
                  <span className="ms-2 text-muted fw-normal">({group.items.length})</span>
                </span>
                <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
              </button>

              {isOpen && (
                <div className="pt-2">
                  <div className="mb-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      disabled={openingId === group.mediaId}
                      onClick={() => onOpen(group.mediaId)}
                    >
                      {openingId === group.mediaId ? 'Opening…' : 'Open file'}
                    </button>
                  </div>
                  <ul className="list-unstyled mb-0">
                    {group.items.map((src, i) => {
                      const section = src.heading_path?.length ? src.heading_path.join(' › ') : null;
                      return (
                        <li key={`${group.mediaId}-${i}`} className="small text-muted mb-1">
                          <i className="bi bi-dot me-1" />
                          {section || src.breadcrumb || sourceLabel(src)}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AssetAiChatPage() {
  const { setTopBarProps } = useOutletContext();
  const { access_key: accessKey } = useParams();
  const { userProfile } = useContext(UserProfileContext);
  const { assetMap, loading: accessLoading, fetchAndValidateAccess } = useContext(AccessContext);
  const asset = assetMap[accessKey]?.data;
  const fieldEngineerId = userProfile?.field_engineer_id;

  const [input, setInput] = useState('');
  const [openingMediaId, setOpeningMediaId] = useState(null);
  const scrollRef = useRef(null);
  const { messages, loading, error, sendMessage, clearChat } = useAssetAiChat({
    accessKey,
    fieldEngineerId,
  });
  const { openDocument, openError: docOpenError } = useAssetMediaPublic(accessKey, fieldEngineerId);

  const starterPrompts = useMemo(() => {
    const prompts = [...DEFAULT_STARTER_PROMPTS];
    const wf = asset?.self_startable_workflows?.[0];
    if (wf?.name) {
      prompts.push(`How do I start "${wf.name}"?`);
    }
    return prompts;
  }, [asset?.self_startable_workflows]);

  useEffect(() => {
    setTopBarProps({
      headerText: asset ? `AI Chat - ${asset.asset_name}` : 'AI Chat',
    });
  }, [setTopBarProps, asset]);

  useEffect(() => {
    if (accessKey) fetchAndValidateAccess(accessKey);
  }, [accessKey, fetchAndValidateAccess]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleOpenSource = async (mediaId) => {
    setOpeningMediaId(mediaId);
    try {
      await openDocument(mediaId);
    } finally {
      setOpeningMediaId(null);
    }
  };

  if (!fieldEngineerId) {
    return (
      <ErrorView
        title="Profile required"
        message="Complete your field engineer profile to use AI chat."
      />
    );
  }

  if (accessLoading && !asset) {
    return <LoadingView title="Loading asset…" fullViewport />;
  }

  if (!asset?.ai_chat_available) {
    return (
      <ErrorView
        title="AI chat unavailable"
        message="No shared documents are indexed for this asset yet. Ask your administrator to share PDF, HTML, TXT, CSV, or Excel files with field engineers."
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    await sendMessage(text);
  };

  return (
    <div className="d-flex flex-column" style={{ height: 'calc(100vh - 150px)', minHeight: 320 }}>
      <div ref={scrollRef} className="flex-grow-1 overflow-auto px-2 py-3">
        <div className="text-center mb-3">
          <div className="d-inline-block bg-primary-subtle border border-primary-subtle rounded px-3 py-2 small text-start">
            <i className="bi bi-robot me-2 text-primary" />
            Hi! I&apos;m your AI assistant for this asset. Ask about manuals, specifications,
            troubleshooting, maintenance history, or workflows you can start on site.
          </div>
        </div>

        {messages.length === 0 && (
          <div className="mb-3 px-1">
            <div className="small text-muted mb-2">Try asking:</div>
            <div className="d-flex flex-wrap gap-2">
              {starterPrompts.map((text) => (
                <button
                  key={text}
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill"
                  disabled={loading}
                  onClick={() => sendMessage(text)}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.length > 0 && (
          <div className="text-end mb-2">
            <button type="button" className="btn btn-link btn-sm text-muted" onClick={clearChat}>
              Clear chat
            </button>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`mb-3 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
          >
            <div
              className={`rounded px-3 py-2 small ${
                msg.role === 'user'
                  ? 'bg-primary text-white'
                  : 'bg-white border text-body'
              }`}
              style={{ maxWidth: '88%' }}
            >
              {msg.role === 'user' ? (
                <div>{msg.content}</div>
              ) : (
                <div>
                  {renderAssistantContent(msg.content)}
                  <ChatSources
                    sources={msg.sources}
                    onOpen={handleOpenSource}
                    openingId={openingMediaId}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="text-muted small px-2">
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Thinking…
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2 small mx-2">{error}</div>
        )}

        {docOpenError && (
          <div className="alert alert-warning py-2 small mx-2">{docOpenError}</div>
        )}
      </div>

      <form className="border-top bg-white p-2 d-flex gap-2 align-items-center" onSubmit={handleSubmit}>
        <FormInput
          id="asset-ai-chat-message"
          name="message"
          type="text"
          placeholder="Ask about this asset…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
          className="mb-0 flex-grow-1"
          inputClassName="rounded-pill"
          autoComplete="off"
          aria-label="Message"
        />
        <button
          type="submit"
          className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 44, height: 44 }}
          disabled={loading || !input.trim()}
          aria-label="Send"
        >
          <i className="bi bi-send-fill" />
        </button>
      </form>
    </div>
  );
}
